import { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
} from '@mui/material'
import { East } from '@mui/icons-material'
import type {
    APIProductCategory,
    APIProductManufacturer,
} from '@visorpro/client'
import fuzzysort from 'fuzzysort'
import _ from 'lodash'

import { requiredFields, type ColumnMapping } from '../../types'

export interface CsvColumnsDialogProps {
    open: boolean
    csvColumns: string[]
    manufactures: APIProductManufacturer[]
    categories: APIProductCategory[]
    isProcessing: boolean
    onConfirm: (
        columnMapping: ColumnMapping,
        manufacturer_id: string,
        category_id: string,
    ) => Promise<void>
    onClose: () => void
}

export const CsvColumnsDialog = ({
    open,
    csvColumns,
    manufactures,
    categories,
    isProcessing,
    onConfirm,
    onClose,
}: CsvColumnsDialogProps) => {
    const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
        original_filename: '',
        sha512_hash: '',
        title: '',
        models: '',
        type: '',
    })
    const [manufacturer_id, setManufacturer] = useState<string>('')
    const [category_id, setCategory] = useState<string>('')

    useEffect(() => {
        if (!open) return

        for (const field of requiredFields) {
            const acceptableNames = field.acceptableNames
            const matches = []

            for (const acceptableName of acceptableNames) {
                const results = fuzzysort.go(acceptableName, csvColumns, {
                    threshold: 0.5,
                })
                matches.push(results.map((result) => result.target))
            }

            const matchFrequency = _.countBy(matches.flat())
            const match =
                _.maxBy(_.entries(matchFrequency), ([, count]) => count)?.[0] ??
                ''

            if (match) {
                setColumnMapping((prev) => ({ ...prev, [field.key]: match }))
            }
        }
    }, [csvColumns, open])

    const valid = useMemo(() => {
        const emptyFields = requiredFields.filter(
            (field) => columnMapping[field.key] === '' && field.required,
        )
        if (emptyFields.length > 0) return true
        if (manufacturer_id === '') return true
        if (category_id === '') return true
        return false
    }, [category_id, columnMapping, manufacturer_id])

    return (
        <Dialog open={open}>
            <DialogTitle>Map Spreadsheet Columns</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Map the following required fields to the corresponding
                    columns in your metadata sheet. It guesss the columns automatically,
                    but you may manually adjust.
                </DialogContentText>
                <List sx={{ px: 0 }}>
                    {requiredFields.map((field, index) => {
                        return (
                            <ListItem key={index} disableGutters>
                                <ListItemText
                                    primary={field.label}
                                    secondary={field.description}
                                />
                                <ListItemIcon sx={{ mx: 2 }}>
                                    <East />
                                </ListItemIcon>
                                <Select
                                    value={columnMapping[field.key]}
                                    label="Column"
                                    sx={{ width: 200, minWidth: 200, maxWidth: 200 }}
                                    required={field.required}
                                    displayEmpty
                                    onChange={(event) => {
                                        const value = event.target.value
                                        setColumnMapping((prev) => ({
                                            ...prev,
                                            [field.key]: value,
                                        }))
                                    }}>
                                    <MenuItem value="">
                                        <Box
                                            sx={{
                                                fontStyle: 'italic',
                                                color: (theme) =>
                                                    theme.palette.grey[500],
                                            }}>
                                            None
                                        </Box>
                                    </MenuItem>
                                    {csvColumns.map((column, index) => (
                                        <MenuItem key={index} value={column}>
                                            {column}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </ListItem>
                        )
                    })}
                    <Divider />
                    <ListItem disableGutters>
                        <ListItemText
                            primary="Manufacture"
                            secondary="Product manufacture"
                        />
                        <Select
                            value={manufacturer_id}
                            label="Column"
                            sx={{ width: 250 }}
                            required
                            displayEmpty
                            onChange={(event) => {
                                const value = event.target.value
                                setManufacturer(value)
                            }}>
                            <MenuItem value="">
                                <Box
                                    sx={{
                                        fontStyle: 'italic',
                                        color: (theme) =>
                                            theme.palette.grey[500],
                                    }}>
                                    None
                                </Box>
                            </MenuItem>
                            {manufactures.map((manufacture) => (
                                <MenuItem
                                    key={manufacture.id}
                                    value={manufacture.id}>
                                    {manufacture.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </ListItem>
                    <ListItem disableGutters>
                        <ListItemText
                            primary="Category"
                            secondary="Product line"
                        />
                        <Select
                            value={category_id}
                            label="Column"
                            sx={{ width: 250 }}
                            required
                            displayEmpty
                            onChange={(event) => {
                                const value = event.target.value
                                setCategory(value)
                            }}>
                            <MenuItem value="">
                                <Box
                                    sx={{
                                        fontStyle: 'italic',
                                        color: (theme) =>
                                            theme.palette.grey[500],
                                    }}>
                                    None
                                </Box>
                            </MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </ListItem>
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    color="primary"
                    variant="outlined"
                    disabled={valid || isProcessing}
                    onClick={() =>
                        void onConfirm(
                            columnMapping,
                            manufacturer_id,
                            category_id,
                        )
                    }>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}
